import React, { useState, useEffect } from "react";
import Select from "react-select";
import axios from "axios";
import { apiUrl } from "../config";
import PageContainer from "../components/custom/PageContainer";
import Main from "../components/custom/Main";
import { MENU_ITEMS } from "../components/custom/SideNavBar";
import Card from "../components/custom/Card";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch } from "../redux/store";
import { StoreState } from "../redux/reducers";
import { DropDownType, fetchDropDown } from "../redux/actions/dropDownAction";
import { BranchType, fetchBranch } from "../redux/actions/branchActions";
import FormInput from "../components/custom/FormInput";
import { AuthUserType } from "../redux/actions/authUserActions";
import { fetchUser } from "../redux/actions/userActions";

type RoleType = {
  id: number;
  role: string;
  branchId: number;
  pageTitle: string;
  pageUrl: string;
  add: boolean;
  view: boolean;
  edit: boolean;
  delete: boolean;
  created_by: string;
  createdAt: string;
};

const dynamicModules = MENU_ITEMS.flatMap((menuItem) => {
  if (menuItem.children && menuItem.children.length > 0) {
    return menuItem.children.map((child) => ({
      pageParentTitle: menuItem.label || "",
      pageTitle: child.label || "",
      pageUrl: child.url || "",
      add: false,
      edit: false,
      view: false,
      delete: false,
    }));
  } else if (menuItem.label !== "Logout" && menuItem.label !== "Profile") {
    return [
      {
        pageTitle: menuItem.label || "",
        pageUrl: menuItem.url || "",
        add: false,
        edit: false,
        view: false,
        delete: false,
      },
    ];
  } else {
    return [];
  }
});

const RoleManagement = () => {
  const location = window.location.pathname;
  const dispatch = useDispatch<AppDispatch>();
  const authUser = useSelector<StoreState, AuthUserType>(
    (state) => state.authUser,
  );
  const dropDownList = useSelector<StoreState, DropDownType[]>(
    (state) => state.dropDown,
  );
  const branchList = useSelector<StoreState, BranchType[]>(
    (state) => state.branch,
  );

  const [menuContent, setMenuContent] = useState(dynamicModules);
  const [selectedRole, setSelectedRole] = useState<
    { value: string; label: string }[]
  >([]);
  const [branchId, setBranchId] = useState<string>("");
  const [branchIdError, setBranchIdError] = useState<string>("");
  const [roleError, setRoleError] = useState<string>("");
  const [id, setId] = useState<number | null>(null);
  const [formType, setFormType] = useState("ADD");

  const canAdd =
    authUser.rolePermissions?.some(
      (rp) => rp.pageUrl === location && rp.add === "true",
    ) || authUser.role === "SUPER";
  const canEdit =
    authUser.rolePermissions?.some(
      (rp) => rp.pageUrl === location && rp.edit === "true",
    ) || authUser.role === "SUPER";
  const canDelete =
    authUser.rolePermissions?.some(
      (rp) => rp.pageUrl === location && rp.delete === "true",
    ) || authUser.role === "SUPER";

  const excludedMenuItems = [
    "Billing",
    "Concession",
    "Miscellaneous",
    "Cancel Billing",
    "Student Fees",
    "Student Fees Summary",
    "Total Fees Summary",
    "Billing Report"
  ];

  useEffect(() => {
    dispatch(fetchDropDown());
    dispatch(fetchBranch());
    dispatch(fetchUser());
  }, []);

  useEffect(() => {
    if (selectedRole.length > 0 && branchId) {
      getRole();
    }
  }, [selectedRole, branchId]);

  const getRole = () => {
    const selectedRoles = selectedRole.map((role) => role.value);
    axios
      .get(
        `${apiUrl}/role_permission/?role=${selectedRoles}&branchId=${branchId}`,
      )
      .then((response) => {
        const rolePermissions = response.data.data;
        if (rolePermissions && rolePermissions.length > 0) {
          setMenuContent((prevMenuContent) =>
            prevMenuContent.map((module) => {
              const roleData = rolePermissions.find(
                (data: RoleType) => data.pageTitle === module.pageTitle,
              );
              return roleData
                ? {
                    ...module,
                    add: roleData.add === "true",
                    edit: roleData.edit === "true",
                    view: roleData.view === "true",
                    delete: roleData.delete === "true",
                  }
                : module;
            }),
          );
          setFormType("EDIT");
        } else {
          setMenuContent(dynamicModules);
          setFormType("ADD");
        }
      })
      .catch((error) => {
        console.error("Error fetching role permissions", error);
      });
  };

  const handleRoleChange = (selectedOption: any) => {
    setSelectedRole(selectedOption || []);
  };

  const handlePermissionChange = (
    pageTitle: string,
    action: string,
    checked: boolean,
  ) => {
    setMenuContent((prevMenuContent) =>
      prevMenuContent.map((module) =>
        module.pageTitle === pageTitle
          ? {
              ...module,
              [action]: checked,
              // If view is being unchecked, uncheck all other permissions
              ...(action === "view" && !checked
                ? {
                    add: false,
                    edit: false,
                    delete: false,
                  }
                : {}),
            }
          : module,
      ),
    );
  };

  const toggleAll = (permissionType: "view" | "add" | "edit" | "delete") => {
    // For view, toggle all including excluded items
    if (permissionType === "view") {
      const allSelected = menuContent.every((menu) => menu.view);
      setMenuContent((prev) =>
        prev.map((menu) => ({
          ...menu,
          view: !allSelected,
          // If turning off view, turn off other permissions too
          ...(!allSelected
            ? {}
            : {
                add: false,
                edit: false,
                delete: false,
              }),
        })),
      );
    }
    // For other permissions, only toggle non-excluded items that have view enabled
    else {
      const relevantItems = menuContent.filter(
        (menu) => !excludedMenuItems.includes(menu.pageTitle) && menu.view,
      );
      const allSelected = relevantItems.every((menu) => menu[permissionType]);

      setMenuContent((prev) =>
        prev.map((menu) => {
          if (excludedMenuItems.includes(menu.pageTitle)) {
            return menu;
          }
          return {
            ...menu,
            [permissionType]: menu.view ? !allSelected : false,
          };
        }),
      );
    }
  };

  const handleSave = () => {
    let error = false;

    const role = selectedRole.map((role) => role.value);

    const roleProperties = menuContent.map((module) => ({
      pageTitle: module.pageTitle,
      pageUrl: module.pageUrl,
      add: module.add,
      edit: module.edit,
      view: module.view,
      delete: module.delete,
      branchId: branchId,
    }));

    if (formType === "ADD") {
      if (!role.length) {
        setRoleError("Role is required");
        error = true;
      }
      if (!branchId) {
        setBranchIdError("Branch is required");
        error = true;
      }
      if (!roleProperties.length) {
        alert("At least one permission must be assigned");
        error = true;
      }
    } else if (formType === "EDIT") {
      if (!role.length) {
        setRoleError("Role is required");
        error = true;
      }
      if (!branchId) {
        setBranchIdError("Branch is required");
        error = true;
      }
      if (!roleProperties.length) {
        alert("At least one permission must be assigned");
        error = true;
      }
    }

    const roleData = {
      id: id,
      role: role,
      branchId: branchId,
      roleProperties: roleProperties,
    };
    console.log("roleData", roleData);

    if (!error) {
      if (formType === "ADD") {
        axios
          .post(`${apiUrl}/role_permission/`, roleData)
          .then(() => {
            alert("Role added successfully");
            dispatch(fetchDropDown());
            resetStates();
          })
          .catch((error) => {
            console.error("Error adding role:", error);
            alert("Error adding role: " + error.message);
          });
      } else if (formType === "EDIT") {
        axios
          .put(`${apiUrl}/role_permission/`, roleData)
          .then(() => {
            alert("Role updated successfully");
            resetStates();
            setFormType("ADD");
          })
          .catch((error) => {
            alert("Error updating role: " + error.message);
          });
      }
    }
  };
  const resetStates = () => {
    setSelectedRole([]);
    setMenuContent(dynamicModules);
    setId(null);
    setRoleError("");
    setBranchId("");
  };

  return (
    <Main>
      <PageContainer title="Role Permission">
        <Card title="">
          <div className="row mb-4">
            <div className="col-sm-6 col-md-4 col-lg-3">
              <label>Select Role</label>
              <Select
                options={dropDownList
                  .filter((dl) => dl.category === "Role")
                  .map((dd) => ({ label: dd.title, value: dd.title }))}
                placeholder="Select Role"
                isMulti
                value={selectedRole}
                onChange={handleRoleChange}
              />
              {roleError && <div className="text-danger">{roleError}</div>}
            </div>
            <div className="col-sm-6 col-md-4 col-lg-3">
              <FormInput
                label="Branch"
                name="Branch"
                labelClassName="required"
                type="select"
                value={branchId}
                onChange={(e) => setBranchId(e.target.value)}
                errorText={branchIdError}
              >
                <option value="">Select</option>
                {branchList.map((branch) => (
                  <option key={branch.id} value={branch.id}>
                    {branch.name}
                  </option>
                ))}
              </FormInput>
            </div>
          </div>

          <h4>Assign Permissions</h4>
          {menuContent.length > 0 ? (
            <table className="table">
              <thead>
                <tr>
                  <th>Menu</th>
                  <th className="text-center">
                    <button
                      className="btn btn-sm btn-link p-0 ms-2 text-white text-decoration-none"
                      onClick={() => toggleAll("view")}
                      title="Toggle all"
                    >
                      <h6 className="m-0">
                        <b>View</b>{" "}
                        <i className="fas fa-check-double text-primary"></i>
                      </h6>
                    </button>
                  </th>
                  <th className="text-center">
                    <button
                      className="btn btn-sm btn-link p-0 ms-2 text-white text-decoration-none"
                      onClick={() => toggleAll("add")}
                      title="Toggle all"
                      style={{ marginLeft: "5px" }}
                    >
                      <h6 className="m-0">
                        <b>Add</b>{" "}
                        <i className="fas fa-check-double text-primary"></i>
                      </h6>
                    </button>
                  </th>
                  <th className="text-center">
                    <button
                      className="btn btn-sm btn-link p-0 ms-2 text-white text-decoration-none"
                      onClick={() => toggleAll("edit")}
                      title="Toggle all"
                    >
                      <h6 className="m-0">
                        <b>Edit</b>{" "}
                        <i className="fas fa-check-double text-primary"></i>
                      </h6>
                    </button>
                  </th>
                  <th className="text-center">
                    <button
                      className="btn btn-sm btn-link p-0 ms-2 text-white text-decoration-none"
                      onClick={() => toggleAll("delete")}
                      title="Toggle all"
                    >
                      <h6 className="m-0">
                        <b>Delete</b>{" "}
                        <i className="fas fa-check-double text-primary"></i>
                      </h6>
                    </button>
                  </th>
                </tr>
              </thead>
              <tbody>
                {menuContent.map((menu) => {
                  const isExcluded = excludedMenuItems.includes(menu.pageTitle);
                  const showOtherPermissions = menu.view && !isExcluded;

                  return (
                    <tr key={menu.pageTitle + menu.pageUrl}>
                      <td>{menu.pageTitle}</td>
                      <td className="text-center align-middle">
                        <div className="d-flex justify-content-center">
                          <input
                            type="checkbox"
                            className="form-check-input m-0"
                            checked={menu.view}
                            onChange={(e) =>
                              handlePermissionChange(
                                menu.pageTitle,
                                "view",
                                e.target.checked,
                              )
                            }
                          />
                        </div>
                      </td>
                      <td className="text-center align-middle">
                        {showOtherPermissions ? (
                          <div className="d-flex justify-content-center">
                            <input
                              type="checkbox"
                              className="form-check-input m-0"
                              checked={menu.add}
                              onChange={(e) =>
                                handlePermissionChange(
                                  menu.pageTitle,
                                  "add",
                                  e.target.checked,
                                )
                              }
                            />
                          </div>
                        ) : (
                          <div className="d-flex justify-content-center">
                            <input
                              type="checkbox"
                              className="form-check-input m-0"
                              checked={false}
                              disabled
                              style={{ opacity: 0.5 }}
                            />
                          </div>
                        )}
                      </td>
                      <td className="text-center align-middle">
                        {showOtherPermissions ? (
                          <div className="d-flex justify-content-center">
                            <input
                              type="checkbox"
                              className="form-check-input m-0"
                              checked={menu.edit}
                              onChange={(e) =>
                                handlePermissionChange(
                                  menu.pageTitle,
                                  "edit",
                                  e.target.checked,
                                )
                              }
                            />
                          </div>
                        ) : (
                          <div className="d-flex justify-content-center">
                            <input
                              type="checkbox"
                              className="form-check-input m-0"
                              checked={false}
                              disabled
                              style={{ opacity: 0.5 }}
                            />
                          </div>
                        )}
                      </td>
                      <td className="text-center align-middle">
                        {showOtherPermissions ? (
                          <div className="d-flex justify-content-center">
                            <input
                              type="checkbox"
                              className="form-check-input m-0"
                              checked={menu.delete}
                              onChange={(e) =>
                                handlePermissionChange(
                                  menu.pageTitle,
                                  "delete",
                                  e.target.checked,
                                )
                              }
                            />
                          </div>
                        ) : (
                          <div className="d-flex justify-content-center">
                            <input
                              type="checkbox"
                              className="form-check-input m-0"
                              checked={false}
                              disabled
                              style={{ opacity: 0.5 }}
                            />
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          ) : (
            <p>No menu content available.</p>
          )}
          <>
            {(formType === "ADD" || formType === "EDIT") && (
              <div className="d-flex justify-content-end hstack gap-1 mt-2">
                <button
                  className="btn btn-sm fw-bold btn-secondary"
                  onClick={resetStates}
                  style={{ marginRight: "5px" }}
                >
                  Clear
                </button>
                {formType === "ADD" && canAdd && (
                  <button
                    className="btn btn-sm fw-bold btn-primary"
                    onClick={handleSave}
                    style={{ backgroundColor: "#10296C", color: "#fff" }}
                  >
                    Save
                  </button>
                )}
                {formType === "EDIT" && canEdit && (
                  <button
                    className="btn btn-sm fw-bold btn-primary"
                    onClick={handleSave}
                    style={{ backgroundColor: "#10296C", color: "#fff" }}
                  >
                    Update
                  </button>
                )}
              </div>
            )}
          </>
        </Card>
      </PageContainer>
    </Main>
  );
};

export default RoleManagement;
